import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography, useMediaQuery } from '@mui/material';
import { RADIUS } from '../../../theme/tokens';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IBannerReponse } from '../../../model/base/response/banner-response';
import { useMenuLinkNavigation } from '../../../hooks/useMenuLinkNavigation';

/**
 * Banner de corpo da home customizada (seção Banners): card informativo na largura do corpo, com a imagem ao lado
 * de um painel na cor do portal. Layout propositalmente diferente do carrossel do topo (HeroBanner).
 */
export default function BodyBanner({ banners }: { banners: IBannerReponse[] }) {
  const [active, setActive] = useState(0);
  const openLink = useMenuLinkNavigation();
  const total = banners.length;
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    if (total < 2 || prefersReducedMotion) return;
    const timer = setInterval(() => setActive((current) => (current + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total, prefersReducedMotion]);

  if (total === 0) return null;

  const banner = banners[active % total];
  const navSx = { width: 44, height: 44, color: 'inherit', border: '1px solid rgba(255,255,255,.5)', '&:hover': { backgroundColor: 'rgba(255,255,255,.15)' } };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
        minHeight: { md: 260 },
        borderRadius: `${RADIUS.block}px`,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        component="img"
        src={banner.urlImage}
        alt={banner.title || ''}
        loading="lazy"
        sx={{ width: '100%', height: '100%', aspectRatio: { xs: '16 / 9', md: 'auto' }, objectFit: 'cover', display: 'block' }}
      />

      <Box
        sx={{
          backgroundColor: 'brand.deep',
          color: 'common.white',
          p: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2,
          minWidth: 0,
        }}
      >
        {banner.title && (
          <Typography
            component="h3"
            sx={{
              fontFamily: (theme) => theme.typography.h1.fontFamily,
              fontSize: { xs: '1.125rem', md: '1.375rem' },
              fontWeight: 600,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {banner.title}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {banner.url && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => openLink(banner.url, banner.openInNewTab)}
              sx={{ backgroundColor: 'common.white', color: 'brand.text', '&:hover': { backgroundColor: 'grey.100' } }}
            >
              Saiba mais
            </Button>
          )}
          {total > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <IconButton aria-label="Banner anterior" sx={navSx} onClick={() => setActive((active - 1 + total) % total)}>
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {(active % total) + 1} / {total}
              </Typography>
              <IconButton aria-label="Próximo banner" sx={navSx} onClick={() => setActive((active + 1) % total)}>
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
